import { LookupItem, MappedLookupItem } from "common/types";
import * as yup from 'yup';

export interface GridSource {
    data: Account[];
    total: number;
    view: Account[];
    filter: {
        searchTerm: string | null;
    };
}

export interface Account {
    userId: string;
    userName: string;
    email: string;
    userRoles: string[];
    userRole: string;
    projects: string[];
    projectCodeNames: string[];
    projectIds: number[];
    maxSegmentsPerRun: number;
    removeCubeLocksTimeUtc: number;
    key?: string;
    isSuperAdmin?: boolean;
}

export interface AccountEditModal {
    open: boolean;
    locked: boolean;
    source: AccountEditModalSource | null;
    serverError: string;
}

export interface AccountMgmtState {
    showGridSpinner: boolean;
    gridSource: GridSource;
    accountEditModal: AccountEditModal;
    passwordResetModal: PasswordResetModal,
}

export interface UserLinks {
    userId: string;
    projects: LookupItem[];
    codes: string[];
}

export interface EditModalLookups {
    projectCodes: MappedMultiselectLookupItem[];
    userRoles: MappedLookupItem[];
    projects: MappedMultiselectLookupItem[];
}

export interface MappedMultiselectLookupItem {
    idx: number;
    displayName: string;
}

export interface AccountEditModalSource {
    userId?: string;
    userName: string | null;
    email: string | null;
    userRoles: MappedLookupItem[];
    userRole: string | null;
    projects: MappedMultiselectLookupItem[];
    projectIds: number[];
    projectCodeNames: string[];
    key?: string;
    password?: string | null;
    maxSegmentsPerRun: number;
    removeCubeLocksTimeUtc: number | null;
    projectCodes: MappedMultiselectLookupItem[];
    isExternal: boolean;
    directoryDomain: string;
    firstName: string;
    lastName: string;
}

export interface PasswordResetModal {
    header: string;
    open: boolean;
    locked: boolean;
    source: {
        currentUser: {
            email: string;
            isSuperAdmin: boolean;
            userId: string;
            userName: string;
            userRoles: string[];
        }
        userId: string | null;
        password: string | null;
        confirmPassword: string | null;
        currentPassword?: string;
    };
}

export interface PasswordResetFormData {
    currentPassword?: string;
    password: string;
    confirmPassword: string;
}

export const passwordResetSchema: yup.AnyObjectSchema = yup.object({
    currentPassword: yup
        .string()
        .test('current-password-required', 'Current password is required', function (value) {
            return this.options.context?.requireCurrentPassword ? Boolean(value) : true;
        }),
    password: yup
        .string()
        .required('Password is required'),
    confirmPassword: yup
        .string()
        .required('Confirm your password')
        .test('passwords-match', 'Passwords mismatch.', function (value) {
            return value === this.parent.password;
        }),
}).required();

export const accountEditSchema: yup.AnyObjectSchema = yup.object({
    firstName: yup
        .string()
        .trim()
        .when('userId', {
            is: (userId: string) => !userId,
            then: schema => schema.required('First Name is required'),
            otherwise: schema => schema.notRequired(),
        }),
    lastName: yup
        .string()
        .trim()
        .when('userId', {
            is: (userId: string) => !userId,
            then: schema => schema.required('Last Name is required'),
            otherwise: schema => schema.notRequired(),
        }),
    password: yup
        .string()
        .nullable()
        .test('password-required', 'Password is required', function (value) {
            const { isExternal, userId } = this.parent as AccountEditModalSource;
            return isExternal || userId != null || Boolean(value);
        }),
    email: yup
        .string()
        .nullable()
        .required('Email is required')
        .test('internal-email-allowed-chars', 'Used characters not allowed', function (value) {
            const { isExternal, directoryDomain } = this.parent as AccountEditModalSource;

            if (isExternal || !directoryDomain || !value) return true;

            const usernameRegex = /^[A-Za-z0-9._-]+$/;
            const [userName] = String(value).split('@');

            return usernameRegex.test(userName);
        })
        .test('external-email-must-not-use-directory-domain', 'External email must not use directory domain', function (value) {
            const { isExternal, directoryDomain } = this.parent as AccountEditModalSource;

            if (!isExternal || !directoryDomain || !value) return true;

            const [, domainRaw = ''] = String(value).split('@');

            return domainRaw.trim().toLowerCase() !== String(directoryDomain).trim().toLowerCase();
        })
        .test('external-email-format', 'Email has invalid format', function (value) {
            const { isExternal } = this.parent as AccountEditModalSource;

            if (!isExternal || !value) return true;

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            return emailRegex.test(String(value).trim());
        }),
    userRole: yup
        .string()
        .nullable()
        .required('User role is required'),
    maxSegmentsPerRun: yup
        .number()
        .required('Max Segments Per Run must be from 1 to 5000')
        .min(1, 'Max Segments Per Run must be from 1 to 5000')
        .max(5000, 'Max Segments Per Run must be from 1 to 5000'),
    removeCubeLocksTimeUtc: yup
        .number()
        .nullable()
        .required('Disconnect Cube Hours must be from 0 to 23')
        .min(0, 'Disconnect Cube Hours must be from 0 to 23')
        .max(23, 'Disconnect Cube Hours must be from 0 to 23'),
}).required();

export interface ChangePasswordRequest {
    password: string;
    currentPassword?: string;
    userId: string;
}

export interface UserSettings {
    maxSegmentsPerRun: number | null;
    removeCubeLocksTimeUtc: string | null;
}
